import { ReactNode } from 'react'
import { QueryClient } from '@tanstack/react-query'
import Big from 'big.js'
import dayjs from 'dayjs'
import { TFunction } from 'react-i18next'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { EarnMyAsset } from '../components/EarnMyAssets'
import {
  EARN_ASSET_STATUS,
  EarnAsset,
  EarnDepositFormData,
  EarnWithdrawFormData,
  MerchantStatistics,
} from '../types'

export const formatAmount = (amount: number) =>
  amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

export const formatUsd = (amount: string | number | null | undefined) =>
  `$${formatAmount(Number(amount ?? 0))}`

export const formatAPY = (rate: number) => `${formatAmount(rate)}%`

export const formatWholePercentage = (rate: number) => `${Math.round(rate)}%`

export const parseNumericValue = (value: ReactNode): number | null => {
  if (typeof value !== 'string') return null
  const parsed = Number(value.replace(/[^0-9.-]/g, ''))
  return Number.isNaN(parsed) ? null : parsed
}

export const toMyEarnAssets =
  (
    t: TFunction<'translation', undefined>,
    onDeposit: (asset: EarnAsset) => void,
    onWithdraw: (asset: EarnAsset) => void,
    statisticsByCurrency: Record<string, MerchantStatistics>
  ) =>
  (asset: EarnAsset): EarnMyAsset => {
    const comingSoon = asset.status === EARN_ASSET_STATUS.COMING_SOON
    const merchantMovableBalance = statisticsByCurrency[asset.asset]?.merchantMovableBalance

    return {
      portfolioId: asset.portfolioId ?? undefined,
      currency: asset.asset,
      productName: asset.productName,
      comingSoon,
      myEarnRate: asset.myEarnRate != null ? formatAPY(asset.myEarnRate) : undefined,
      customerEarnRate:
        asset.customerEarnRate != null ? formatAPY(asset.customerEarnRate) : undefined,
      allocatedFundsAmount:
        asset.balance && !comingSoon
          ? `${formatAmount(Number(asset.balance))} ${asset.asset}`
          : undefined,
      allocatedFundsLabel: asset.earningSince
        ? t('earn.earningSince', { date: dayjs(asset.earningSince).format("MMM ['']YY") })
        : undefined,
      unallocatedFundsAmount: merchantMovableBalance
        ? `${formatAmount(Number(merchantMovableBalance.amount))} ${merchantMovableBalance.currency}`
        : undefined,
      onWithdrawClick: comingSoon ? undefined : () => onWithdraw(asset),
      onDepositClick: comingSoon ? undefined : () => onDeposit(asset),
    }
  }

export const isValidAmount = (value: string) => {
  if (!value || typeof value !== 'string') return false

  const cleanedValue = value.replaceAll(',', '').trim()
  const amountPattern = /^(?!0$)\d+(\.\d+)?$/

  if (!amountPattern.test(cleanedValue)) return false

  const numValue = Number.parseFloat(cleanedValue)
  return numValue > 0
}

export const isValidAmountWithMax = (value: string, maxAmount: string) => {
  if (!value) return true
  if (!maxAmount || maxAmount === '0') return true

  const cleanedValue = value.replaceAll(',', '').trim()

  try {
    // Use Big.js for high-precision decimal comparison
    const bigValue = new Big(cleanedValue)
    const bigMaxAmount = new Big(maxAmount)

    // Compare using Big.js: returns true if value <= maxAmount
    return bigValue.lte(bigMaxAmount)
  } catch {
    // If Big.js throws (invalid number format), return false
    return false
  }
}

export const invalidateEarnQueries = (reactQueryClient: QueryClient, portfolioId?: string) => {
  reactQueryClient.invalidateQueries({
    queryKey: reactQueryKeys.earnDashboard(),
  })

  reactQueryClient.invalidateQueries({
    queryKey: reactQueryKeys.earnWallets({}),
  })

  if (portfolioId) {
    reactQueryClient.invalidateQueries({
      queryKey: reactQueryKeys.earnAssetDetails(portfolioId),
    })
    reactQueryClient.invalidateQueries({
      queryKey: reactQueryKeys.earnAssetTransactions({ portfolioId }),
    })
  }
}

export const prepareDepositReviewData = (data: EarnDepositFormData) => {
  return [
    {
      id: 'depositInto',
      label: 'earn.depositInto',
      value: data.currency,
    },
    {
      id: 'depositFromWallet',
      label: 'earn.depositSheet.depositFromWallet',
      value: data.allocateFrom?.name || '',
    },
    {
      id: 'amount',
      label: 'amount',
      value: `${data.amount} ${data.currency}`,
    },
  ]
}

export const prepareWithdrawReviewData = (data: EarnWithdrawFormData) => {
  return [
    {
      id: 'withdrawFromAsset',
      label: 'earn.withdrawSheet.withdrawFromAsset',
      value: data.currency,
    },
    {
      id: 'amount',
      label: 'amount',
      value: `${data.amount} ${data.currency}`,
    },
    {
      id: 'withdrawToWallet',
      label: 'earn.withdrawSheet.withdrawToWallet',
      value: data.withdrawToWallet?.name || '',
    },
  ]
}
